import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import CarCard from '../components/CarCard';
import { Spinner, Pagination, EmptyState, Button, T } from '../components/UI';

const MAKES = ['Volkswagen','BMW','Audi','Toyota','Ford','Mercedes-Benz','Hyundai','Nissan','Kia','Skoda','Peugeot','Renault','Opel','Volvo','Honda','SEAT','Mazda','Suzuki','Mini','Fiat','Tesla','Land Rover','Jaguar','Jeep'];
const BODY_TYPES = ['Hatchback','Saloon','SUV','Estate','Coupe','Convertible','MPV','Van'];
const FUEL_TYPES = ['Petrol','Diesel','Electric','Petrol Hybrid','Diesel Hybrid','Petrol Plug-in Hybrid'];
const TRANSMISSIONS = ['Manual','Automatic','Semi-Automatic'];
const YEARS = Array.from({ length: 12 }, (_, i) => 2025 - i);
const PRICES = [5000,8000,10000,12000,15000,20000,25000,30000,40000,50000,75000];
const MILEAGE = [10000,20000,30000,50000,75000,100000,150000];
const SORT_OPTS = [
  { value: '',           label: 'Latest first' },
  { value: 'priceasc',   label: 'Price: low to high' },
  { value: 'pricedesc',  label: 'Price: high to low' },
  { value: 'yeardesc',   label: 'Year: newest first' },
  { value: 'mileageasc', label: 'Mileage: lowest first' },
];

function FilterGroup({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: `1px solid ${T.surface}`, paddingBottom: 18, marginBottom: 18 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontSize: 'var(--step-xs)', fontWeight: 600, color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'inherit' }}>
        {title} <span style={{ color: T.muted, fontSize: 10, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
      </button>
      {open && <div style={{ marginTop: 14 }}>{children}</div>}
    </div>
  );
}

function CheckList({ options, selected, onChange, max = 999 }) {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? options : options.slice(0, max);
  const isArr = Array.isArray(selected);
  return (
    <div>
      {shown.map(opt => {
        const val = typeof opt === 'object' ? opt.value : opt;
        const label = typeof opt === 'object' ? opt.label : opt;
        const checked = isArr ? selected.includes(val) : selected === val;
        return (
          <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '5px 0', fontSize: 'var(--step-sm)', color: checked ? T.ink : T.muted }}>
            <span style={{ width: 16, height: 16, border: `1px solid ${checked ? T.accent : T.surface}`, background: checked ? T.accent : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
              {checked && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            </span>
            <input type="checkbox" checked={checked} onChange={() => {
              if (isArr) onChange(checked ? selected.filter(s => s !== val) : [...selected, val]);
              else onChange(checked ? '' : val);
            }} style={{ display: 'none' }} />
            {label}
          </label>
        );
      })}
      {options.length > max && (
        <button onClick={() => setShowAll(s => !s)}
          style={{ background: 'none', border: 'none', color: T.accent, fontSize: 'var(--step-xs)', cursor: 'pointer', padding: '6px 0', fontWeight: 600, fontFamily: 'inherit', letterSpacing: '0.04em' }}>
          {showAll ? 'Show less ▲' : `+ ${options.length - max} more`}
        </button>
      )}
    </div>
  );
}

export default function Stock() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [filters, setFilters] = useState({
    q: searchParams.get('q') || '',
    make: searchParams.getAll('make') || [],
    minYear: searchParams.get('minYear') || '',
    maxYear: searchParams.get('maxYear') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    maxMileage: searchParams.get('maxMileage') || '',
    fuelType: searchParams.getAll('fuelType') || [],
    bodyType: searchParams.getAll('bodyType') || [],
    transmission: searchParams.get('transmission') || '',
    sort: searchParams.get('sort') || '',
    page: parseInt(searchParams.get('page') || '1'),
  });

  const upd = key => val => setFilters(f => ({ ...f, [key]: val, page: 1 }));
  const clearAll = () => setFilters({ q:'', make:[], minYear:'', maxYear:'', minPrice:'', maxPrice:'', maxMileage:'', fuelType:[], bodyType:[], transmission:'', sort:'', page:1 });

  const activeCount = [
    filters.make.length, filters.fuelType.length, filters.bodyType.length,
    filters.minYear, filters.maxYear, filters.minPrice, filters.maxPrice,
    filters.maxMileage, filters.transmission, filters.q,
  ].filter(Boolean).length;

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: 12 };
      if (!params.make.length) delete params.make;
      if (!params.fuelType.length) delete params.fuelType;
      if (!params.bodyType.length) delete params.bodyType;
      const res = await api.getListings(params);
      setListings(res.listings);
      setTotal(res.total);
      setTotalPages(res.totalPages);
    } catch {}
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  useEffect(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach(val => p.append(k, val));
      else if (v) p.set(k, v);
    });
    setSearchParams(p, { replace: true });
  }, [filters, setSearchParams]);

  const selS = { border: `1px solid ${T.surface}`, background: T.bg, padding: '9px 28px 9px 10px', fontSize: 'var(--step-xs)', color: T.ink, appearance: 'none', cursor: 'pointer', width: '100%', outline: 'none', fontFamily: 'inherit' };

  return (
    <div style={{ paddingTop: 68 }}>
      {/* Page header */}
      <div style={{ background: T.ink, paddingBlock: 'clamp(40px, 6vw, 72px)', paddingInline: 'var(--margin)' }}>
        <div style={{ maxWidth: 1680, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 20 }}>
          <div>
            <p style={{ fontSize: 'var(--step-xs)', color: T.accent, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10 }}>All vehicles</p>
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', fontWeight: 400, color: T.bg, lineHeight: 1.05, letterSpacing: '-0.02em', margin: 0 }}>Our Stock</h1>
          </div>
          <p style={{ color: 'rgba(232,230,225,0.5)', fontSize: 'var(--step-sm)' }}>All cars imported directly from the UK — registered in Ireland</p>
        </div>
      </div>

      <div style={{ maxWidth: 1680, margin: '0 auto', paddingInline: 'var(--margin)', paddingBlock: 28, display: 'grid', gridTemplateColumns: sidebarOpen ? '240px 1fr' : '1fr', gap: 32, alignItems: 'start' }}>

        {/* Sidebar */}
        {sidebarOpen && (
          <aside style={{ background: T.bg, border: `1px solid ${T.surface}`, padding: '24px 20px', position: 'sticky', top: 84, maxHeight: 'calc(100vh - 104px)', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <span style={{ fontSize: 'var(--step-xs)', fontWeight: 600, color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Filters {activeCount > 0 && <span style={{ background: T.accent, color: T.accentInk, borderRadius: '50%', width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, marginLeft: 6 }}>{activeCount}</span>}
              </span>
              {activeCount > 0 && <button onClick={clearAll} style={{ background: 'none', border: 'none', color: T.accent, fontSize: 'var(--step-xs)', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', letterSpacing: '0.04em' }}>Clear all</button>}
            </div>

            {/* Quick toggles */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${T.surface}` }}>
              {[{ l: '⚡ Electric', k: 'fuelType', v: 'Electric' }, { l: '⚙ Automatic', k: 'transmission', v: 'Automatic' }].map(t => {
                const on = Array.isArray(filters[t.k]) ? filters[t.k].includes(t.v) : filters[t.k] === t.v;
                return (
                  <button key={t.l} onClick={() => {
                    if (Array.isArray(filters[t.k])) upd(t.k)(on ? filters[t.k].filter(x => x !== t.v) : [...filters[t.k], t.v]);
                    else upd(t.k)(on ? '' : t.v);
                  }} style={{ padding: '6px 14px', border: `1px solid ${on ? T.accent : T.surface}`, background: on ? T.accent : 'transparent', color: on ? T.accentInk : T.muted, fontSize: 'var(--step-xs)', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', letterSpacing: '0.04em' }}>
                    {t.l}
                  </button>
                );
              })}
            </div>

            <FilterGroup title="Make"><CheckList options={MAKES} selected={filters.make} onChange={upd('make')} max={7} /></FilterGroup>
            <FilterGroup title="Year">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[['From','minYear'],['To','maxYear']].map(([ph, key]) => (
                  <div key={key} style={{ position: 'relative' }}>
                    <select style={selS} value={filters[key]} onChange={e => upd(key)(e.target.value)}>
                      <option value="">{ph}</option>
                      {YEARS.map(y => <option key={y}>{y}</option>)}
                    </select>
                    <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: T.muted, fontSize: 9 }}>▼</span>
                  </div>
                ))}
              </div>
            </FilterGroup>
            <FilterGroup title="Price">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[['Min','minPrice'],['Max','maxPrice']].map(([ph,key]) => (
                  <div key={key} style={{ position: 'relative' }}>
                    <select style={selS} value={filters[key]} onChange={e => upd(key)(e.target.value)}>
                      <option value="">{ph}</option>
                      {PRICES.map(p => <option key={p} value={p}>€{p.toLocaleString()}</option>)}
                    </select>
                    <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: T.muted, fontSize: 9 }}>▼</span>
                  </div>
                ))}
              </div>
            </FilterGroup>
            <FilterGroup title="Max Mileage">
              <div style={{ position: 'relative' }}>
                <select style={selS} value={filters.maxMileage} onChange={e => upd('maxMileage')(e.target.value)}>
                  <option value="">Any mileage</option>
                  {MILEAGE.map(m => <option key={m} value={m}>{m.toLocaleString()} km</option>)}
                </select>
                <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: T.muted, fontSize: 9 }}>▼</span>
              </div>
            </FilterGroup>
            <FilterGroup title="Fuel Type"><CheckList options={FUEL_TYPES} selected={filters.fuelType} onChange={upd('fuelType')} /></FilterGroup>
            <FilterGroup title="Body Type"><CheckList options={BODY_TYPES} selected={filters.bodyType} onChange={upd('bodyType')} /></FilterGroup>
            <FilterGroup title="Transmission" defaultOpen={false}><CheckList options={TRANSMISSIONS} selected={filters.transmission ? [filters.transmission] : []} onChange={v => upd('transmission')(v[v.length-1]||'')} /></FilterGroup>
          </aside>
        )}

        {/* Main */}
        <main>
          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${T.surface}`, flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button onClick={() => setSidebarOpen(o => !o)}
                style={{ background: 'none', border: `1px solid ${T.surface}`, padding: '8px 14px', fontSize: 'var(--step-xs)', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: T.muted, letterSpacing: '0.04em', fontFamily: 'inherit', transition: 'border-color 0.15s, color 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.ink; e.currentTarget.style.color = T.ink; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.surface; e.currentTarget.style.color = T.muted; }}>
                {sidebarOpen ? '← Hide' : '→ Filters'}
                {activeCount > 0 && <span style={{ background: T.accent, color: T.accentInk, borderRadius: '50%', width: 17, height: 17, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>{activeCount}</span>}
              </button>
              <span style={{ color: T.muted, fontSize: 'var(--step-sm)' }}>
                {loading ? '—' : <><strong style={{ color: T.ink, fontWeight: 600 }}>{total}</strong> cars</>}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 'var(--step-xs)', color: T.muted, letterSpacing: '0.04em' }}>Sort</span>
              <div style={{ position: 'relative' }}>
                <select value={filters.sort} onChange={e => upd('sort')(e.target.value)}
                  style={{ border: `1px solid ${T.surface}`, background: T.bg, padding: '8px 28px 8px 12px', fontSize: 'var(--step-xs)', color: T.ink, appearance: 'none', cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.02em' }}>
                  {SORT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: T.muted, fontSize: 9 }}>▼</span>
              </div>
            </div>
          </div>

          {/* Active filter chips */}
          {activeCount > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
              {filters.make.map(m => (
                <span key={m} style={{ background: T.ink, color: T.bg, padding: '4px 12px', fontSize: 'var(--step-xs)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8, letterSpacing: '0.04em' }}>
                  {m}
                  <button onClick={() => upd('make')(filters.make.filter(x=>x!==m))} style={{ background:'none',border:'none',color:T.bg,cursor:'pointer',fontSize:14,lineHeight:1,padding:0 }}>×</button>
                </span>
              ))}
              {filters.fuelType.map(f => (
                <span key={f} style={{ background: T.accent, color: T.accentInk, padding: '4px 12px', fontSize: 'var(--step-xs)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {f}
                  <button onClick={() => upd('fuelType')(filters.fuelType.filter(x=>x!==f))} style={{ background:'none',border:'none',color:T.accentInk,cursor:'pointer',fontSize:14,lineHeight:1,padding:0 }}>×</button>
                </span>
              ))}
              {filters.maxPrice && <span style={{ background: T.surface, color: T.muted, padding: '4px 12px', fontSize: 'var(--step-xs)' }}>Up to €{Number(filters.maxPrice).toLocaleString()}</span>}
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div style={{ display:'flex', justifyContent:'center', padding:'80px 0' }}><Spinner size={36} /></div>
          ) : listings.length === 0 ? (
            <EmptyState icon="○" title="No cars match your filters" message="Try clearing some filters to see more results." action={<Button onClick={clearAll} variant="dark">Clear all filters</Button>} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 2 }}>
              {listings.map(car => <CarCard key={car.id} car={car} />)}
            </div>
          )}

          <Pagination page={filters.page} totalPages={totalPages} onChange={p => setFilters(f => ({ ...f, page: p }))} />
        </main>
      </div>
    </div>
  );
}
